import { useSyncExternalStore } from "react";
const WHITE = "#ffffff";
const BLACK = "#000000";
/** dark text, --text-primary of the light theme */
const INK = "#111827";
/** the dark canvas the dark header leans toward */
const NAVY = "#0f172a";
/** the light canvas, under the accent text */
const CANVAS = "#eef3f8";
/** the dark panels, under the dark accent text */
const PANEL_DARK = "#1e293b";
const TITLE_DARK = "#f8fafc";
const AA = 4.5;
/** "#rgb" or "#rrggbb" (trimmed, any case) as lowercase "#rrggbb"; anything else is null. */
export function parseHexColor(value) {
    if (typeof value !== "string")
        return null;
    const v = value.trim().toLowerCase();
    if (/^#[0-9a-f]{6}$/.test(v))
        return v;
    if (/^#[0-9a-f]{3}$/.test(v))
        return "#" + [...v.slice(1)].map((c) => c + c).join("");
    return null;
}
// an sRGB channel (0..1) and linear light, the curve of WCAG 2.x and OKLab
const toLinear = (c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const toGamma = (c) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);
/** "#rrggbb" as linear sRGB. */
function linear(c) {
    const n = parseInt(c.slice(1), 16);
    return [toLinear(((n >> 16) & 255) / 255), toLinear(((n >> 8) & 255) / 255), toLinear((n & 255) / 255)];
}
/** Linear sRGB as "#rrggbb", each channel clamped to 0..1. */
function hex(rgb) {
    const byte = (c) => Math.round(toGamma(Math.min(1, Math.max(0, c))) * 255);
    return "#" + rgb.map((c) => byte(c).toString(16).padStart(2, "0")).join("");
}
// OKLab and back, Björn Ottosson's matrices for linear sRGB
// (https://bottosson.github.io/posts/oklab/, revision of 2021-01-25)
function toOklab([r, g, b]) {
    const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    return [
        0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
        1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
        0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
    ];
}
function fromOklab([L, a, b]) {
    const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
    const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
    const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
    return [
        4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
        -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
        -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
    ];
}
/** a and b mixed in OKLab: t = 0 is a, t = 1 is b. */
function mix(a, b, t) {
    const x = toOklab(linear(a));
    const y = toOklab(linear(b));
    return hex(fromOklab([x[0] + (y[0] - x[0]) * t, x[1] + (y[1] - x[1]) * t, x[2] + (y[2] - x[2]) * t]));
}
/** WCAG 2.x relative luminance of "#rrggbb". */
function luminance(c) {
    const [r, g, b] = linear(c);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** WCAG 2.x contrast ratio of two colours, 1 to 21 (1 when one is not a hex colour). */
export function contrastRatio(a, b) {
    const x = parseHexColor(a);
    const y = parseHexColor(b);
    if (!x || !y)
        return 1;
    const lx = luminance(x);
    const ly = luminance(y);
    return (Math.max(lx, ly) + 0.05) / (Math.min(lx, ly) + 0.05);
}
/**
 * Text on the colours: white, else #111827, when it reaches 4.5:1 on all of
 * them; else whichever of white, #111827 and black does best on the worst one.
 */
function pickOn(colours) {
    const worst = (ink) => Math.min(...colours.map((c) => contrastRatio(ink, c)));
    if (worst(WHITE) >= AA)
        return WHITE;
    if (worst(INK) >= AA)
        return INK;
    return [WHITE, INK, BLACK].reduce((best, ink) => (worst(ink) > worst(best) ? ink : best));
}
/**
 * c when it reaches `ratio` on bg; else the least mix of c toward `toward`
 * that does (binary search, 24 steps); `toward` when none does.
 */
function readable(c, toward, bg, ratio = AA) {
    if (contrastRatio(c, bg) >= ratio)
        return c;
    if (contrastRatio(toward, bg) < ratio)
        return toward;
    let lo = 0;
    let hi = 1;
    for (let i = 0; i < 24; i++) {
        const t = (lo + hi) / 2;
        if (contrastRatio(mix(c, toward, t), bg) >= ratio)
            hi = t;
        else
            lo = t;
    }
    return mix(c, toward, hi);
}
/** The Focke header geometry with three colours. */
const gradient = (a, b, c) => `linear-gradient(287.56deg, ${a} 27.21%, ${b} 43.79%, ${c} 61.44%)`;
/**
 * The brand variables of both themes for a primary colour and the sidebar's
 * secondary colour (unset: the primary 30 % darker). null when primary is
 * not a hex colour.
 */
export function brandingPalette(primary, secondary) {
    const p = parseHexColor(primary);
    if (!p)
        return null;
    const s = parseHexColor(secondary) ?? mix(p, BLACK, 0.3);
    const onBrand = pickOn([p]);
    const brand = {
        brand: p,
        brandBright: [mix(p, WHITE, 0.14), mix(p, BLACK, 0.14)].find((c) => contrastRatio(onBrand, c) >= AA) ?? p,
        brandLight: mix(p, WHITE, 0.55),
        onBrand,
    };
    // ink of the active header button, which is drawn in the on-header colour
    const headerInk = (onHeader) => (onHeader === WHITE ? readable(p, BLACK, WHITE) : p);
    const onHeader = pickOn([p]);
    const accent = readable(p, BLACK, CANVAS);
    const light = {
        ...brand,
        headerGradient: gradient(p, mix(p, WHITE, 0.42), p),
        onHeader,
        headerInk: headerInk(onHeader),
        accent,
        accentDark: mix(accent, BLACK, 0.25),
        accentLight: mix(accent, WHITE, 0.6),
        focus: accent,
        title: mix(accent, BLACK, 0.15),
        sidebar: s,
        onSidebar: pickOn([s]),
    };
    const base = mix(p, NAVY, 0.62);
    const edge = mix(p, NAVY, 0.5);
    const darkOnHeader = pickOn([base, edge]);
    const darkAccent = readable(p, WHITE, PANEL_DARK);
    const dark = {
        ...brand,
        headerGradient: gradient(base, mix(base, WHITE, 0.18), edge),
        onHeader: darkOnHeader,
        headerInk: headerInk(darkOnHeader),
        accent: darkAccent,
        accentDark: mix(darkAccent, BLACK, 0.3),
        accentLight: mix(darkAccent, WHITE, 0.3),
        focus: darkAccent,
        title: TITLE_DARK,
        // the sidebar keeps its colour in both themes, like the Focke default
        sidebar: light.sidebar,
        onSidebar: light.onSidebar,
    };
    return { light, dark };
}
const declarations = (t) => [
    `--color-brand:${t.brand}`,
    `--color-brand-bright:${t.brandBright}`,
    `--color-brand-light:${t.brandLight}`,
    `--on-brand:${t.onBrand}`,
    `--header-gradient:${t.headerGradient}`,
    `--on-header:${t.onHeader}`,
    `--header-ink:${t.headerInk}`,
    `--cockpit-blue:${t.accent}`,
    `--cockpit-blue-dark:${t.accentDark}`,
    `--cockpit-blue-light:${t.accentLight}`,
    `--interactive-focus:${t.focus}`,
    `--title:${t.title}`,
    `--sidebar-bg:${t.sidebar}`,
    `--on-sidebar:${t.onSidebar}`,
].join(";");
/**
 * The stylesheet of a palette. `:root:root` (0,2,0) beats `:root` and `.dark`
 * of tokens.css, and an unlayered sheet beats Tailwind's `@layer theme`.
 * `.dark` is on <html> (initTheme) or on <body> (the cockpit): both match.
 */
export function brandingCss(palette) {
    const font = `--font-brand:var(--font-sans,"IBM Plex Sans",ui-sans-serif,system-ui,sans-serif)`;
    return (`:root:root{${declarations(palette.light)};${font}}\n` +
        `:root:root.dark,:root:root .dark{${declarations(palette.dark)};--accent-on-dark:${palette.dark.accent}}\n`);
}
// ---- the store (like the theme in theme.ts) ---------------------------------
const STYLE_ID = "enlixy-branding";
let storageKey = "enlixy-branding";
let current = null;
const listeners = new Set();
// https, a dev server on localhost, a path on this origin (not "//" or "/\",
// which name another host), an object URL or an inline PNG
const LOGO_URL = /^(?:https:\/\/|http:\/\/localhost(?::\d{1,5})?(?:\/|$)|\/(?![/\\])|blob:|data:image\/png;base64,)/;
/** The valid fields of a branding; null when neither a primary colour nor a logo remains. */
function normalise(value) {
    if (!value || typeof value !== "object")
        return null;
    const v = value;
    const primary = parseHexColor(v.primary);
    const secondary = primary ? parseHexColor(v.secondary) : null;
    const logoUrl = typeof v.logoUrl === "string" && LOGO_URL.test(v.logoUrl) ? v.logoUrl : null;
    if (!primary && !logoUrl)
        return null;
    const out = {};
    const name = typeof v.name === "string" ? [...v.name.trim()].slice(0, 120).join("").trim() : "";
    if (name)
        out.name = name;
    if (primary)
        out.primary = primary;
    if (secondary)
        out.secondary = secondary;
    if (logoUrl)
        out.logoUrl = logoUrl;
    if (typeof v.logoPlate === "boolean")
        out.logoPlate = v.logoPlate;
    if (v.logoSize === "s" || v.logoSize === "m" || v.logoSize === "l")
        out.logoSize = v.logoSize;
    return out;
}
function writeStyle(css) {
    if (typeof document === "undefined")
        return;
    let el = document.getElementById(STYLE_ID);
    if (!css) {
        el?.remove();
        return;
    }
    if (!el) {
        el = document.createElement("style");
        el.id = STYLE_ID;
        document.head.appendChild(el);
    }
    if (el.textContent !== css)
        el.textContent = css;
}
function set(branding, persist) {
    const palette = branding?.primary ? brandingPalette(branding.primary, branding.secondary) : null;
    writeStyle(palette ? brandingCss(palette) : null);
    if (persist) {
        try {
            if (branding)
                localStorage.setItem(storageKey, JSON.stringify(branding));
            else
                localStorage.removeItem(storageKey);
        }
        catch {
            /* private mode: applies to this page only */
        }
    }
    current = branding;
    listeners.forEach((l) => l());
}
/**
 * Applies an organization's branding (null: the Focke default) and stores
 * it for the next visit. Invalid fields are dropped: colours must be hex
 * colours, the logo an https, localhost, same-origin, blob or inline PNG URL.
 */
export function applyBranding(branding) {
    set(normalise(branding), true);
}
/** Back to the Focke default. */
export function clearBranding() {
    applyBranding(null);
}
/**
 * Applies the stored branding before the first render (no flash of the
 * Focke look). Call once in main.tsx, next to initTheme(). storageKey: an
 * app's own key.
 */
export function initBranding(opts = {}) {
    if (opts.storageKey)
        storageKey = opts.storageKey;
    let stored = null;
    try {
        const raw = localStorage.getItem(storageKey);
        stored = raw ? JSON.parse(raw) : null;
    }
    catch {
        stored = null;
    }
    set(normalise(stored), false);
}
/** The branding in effect: the same object until the next applyBranding(). */
export function currentBranding() {
    return current;
}
export function subscribeBranding(cb) {
    listeners.add(cb);
    return () => {
        listeners.delete(cb);
    };
}
/** The branding in effect; the component renders again when it changes. */
export function useBranding() {
    return useSyncExternalStore(subscribeBranding, currentBranding);
}
